// ABOUTME: Entity card for the country configurations overview page.
// ABOUTME: Displays an entity's title, description, scope-level icons, and configured field pills; clickable to navigate to detail.
import {DetailsCard, H3, Icon, Pill, TextSmallRegular} from '@hm/fundamental'
import {CountryConfigsTooltip} from './CountryConfigsTooltip'
import React from 'react'

import {useTranslation} from '../../../../hooks/useTranslation'
import {EntityCardProps} from '../types'
import {SCOPE_LEVEL_OPTIONS} from '../utils'

export const highlightText = (text: string, highlight?: string): React.ReactNode => {
  if (!highlight || !highlight.trim()) return text
  const escaped = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'))
  return parts.map((part, i) =>
    part.toLowerCase() === highlight.toLowerCase() ? (
      <mark key={i} className="bg-primary_faded_20 font-semibold rounded-sm px-0.5">{part}</mark>
    ) : (
      part
    )
  )
}

export const EntityCard: React.FC<EntityCardProps> = ({
  title,
  description,
  configuredFields,
  applicableLevels,
  searchHighlight,
  onClick,
  'data-cy': dataCy
}) => {
  const {t} = useTranslation()
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && onClick) {
      e.preventDefault()
      onClick()
    }
  }

  return (
    <DetailsCard
      data-cy={dataCy}
      className={`rounded-lg border border-border bg-container_background shadow-[0_2px_32px_0_rgba(0,0,0,0.08),0_4px_4px_0_rgba(0,0,0,0.04)] transition-all${onClick ? ' cursor-pointer hover:bg-primary_faded_10' : ''}`}
    >
      <div
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        aria-label={onClick ? title : undefined}
        onClick={onClick}
        onKeyDown={onClick ? handleKeyDown : undefined}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-0.5 min-w-0">
            <H3 className="h-8 flex items-center">{highlightText(title, searchHighlight)}</H3>
            <TextSmallRegular className="text-foreground_faded !font-normal !leading-4">{highlightText(description, searchHighlight)}</TextSmallRegular>
          </div>

          {applicableLevels && applicableLevels.length > 0 && (
            <div className="flex items-center gap-2 shrink-0">
              {applicableLevels.map((level) => {
                const opt = SCOPE_LEVEL_OPTIONS.find((o) => o.value === level)
                if (!opt) return null
                return (
                  <CountryConfigsTooltip key={level} content={t(opt.tooltipKey)} delayMs={200}>
                    <div
                      className="flex items-center justify-center w-6 h-6 rounded-full border border-primary_faded_20 bg-primary_faded_10 text-primary"
                    >
                      <Icon path={opt.icon} size="regular" />
                    </div>
                  </CountryConfigsTooltip>
                )
              })}
            </div>
          )}
        </div>

        {configuredFields.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {configuredFields.map((field) => (
              <Pill key={field.name} type={field.isOverridden ? 'warning' : 'primary'} text={field.name} />
            ))}
          </div>
        )}
      </div>
    </DetailsCard>
  )
}

